import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import type { Transport } from "@tdelivr/api-client";
import { services } from "@tdelivr/api-client";
// The row shapes DSP-02 and DRV-01 publish, imported rather than restated. A column here that a
// service stopped returning is a build failure, not a blank cell.
import type { RideOffer } from "@tdelivr/api-client/generated/dispatch/ride-offer.js";
import type { DriverProfile } from "@tdelivr/api-client/generated/driver/driver-profile.js";

import {
  boundedNote,
  driverCell,
  effectiveStatus,
  lapsed,
  offerDetail,
  offerPage,
  readableStatus,
  resultText,
  FILTERABLE_STATUSES,
  type OfferOrder,
  type OfferStatus,
} from "../offer-monitor.ts";
import { stateForError, stateForList, type ViewState } from "../view-state.ts";
import { PageHeading } from "./PageHeading.tsx";
import { RecordDetail } from "./RecordDetail.tsx";
import { StateBadge } from "./StateBadge.tsx";
import { StateView } from "./StateView.tsx";

/**
 * The offer monitor: offers sent to drivers, and what happened to each.
 *
 * Built to `19 WP18 Dispatch Operations.dc.html`, page `offers`. Every measurement is the one that
 * file computes for its own template, read from the rendered page rather than from a screenshot:
 * the page body is a 16px-gap grid padded `20px 22px 28px`; the data grid is a 16px-radius card
 * holding a `12px 14px` toolbar, a `10px 16px` header strip on `--sunken`, `12px 16px` rows and a
 * `10px 14px` pager; the grid's tracks are its own `minmax(0, 1.5fr)` then one `minmax(0, 1fr)` per
 * remaining column then `190px`, 14px apart; and the closing note is a 14px-radius `--t-sky`
 * callout with 20px of glyph and 13.5/20 text.
 *
 * **All three of the design's columns have a source, which no screen on this console has managed
 * before.** `Offer` is `offerId`; `Driver` is `candidateRef.driverId` joined to DRV-01's
 * `displayName`, the join R-09 established for the live map's roster; `Result` is derived from the
 * five timestamps DSP-02 stamps beside `createdAt`. What is absent is the design's four KPI tiles
 * and the `· too far` on its declined row, and R-10 in `docs/design/DESIGN_RECONCILIATION.md` is
 * the measurement for each.
 *
 * **The status on the wire is behind the clock, and this screen does not repeat it.** DSP-02
 * expires offers with a batched sweeper, so a row whose window has closed still reads `delivered`
 * until the sweep reaches it — while the domain already refuses every transition on it with a 409.
 * Drawing the stored value would put a dead offer under "waiting on the driver" on the one page
 * whose purpose is to show what is about to lapse unanswered. `lapsed()` in `offer-monitor.ts`
 * holds the rule with the evidence beside it.
 *
 * **The state filter is a query parameter, not a filter over the rows.** DSP-02's listing defaults
 * to its live three, so a filter built from the rows on screen — which is what the design does over
 * its three fixed samples — would offer three options and let an operator conclude that no offer
 * was ever accepted. Choosing a state refetches.
 */

const offersApi = services.dispatch__ride_offer;
const profilesApi = services.driver__driver_profile;

/**
 * How many rows to take.
 *
 * 200 is DSP-02's own maximum `limit`, and its contract says so in as many words: "capped at 200 —
 * more than this board shows". A tenant with more than 200 offers in one status at once is in a
 * state this screen cannot help with, and the honest thing then is to say the page is partial.
 */
const PER_FETCH = "200";

/** The design's own rows-per-page choices, in its order. */
const PER_PAGE_CHOICES = [5, 10, 25, 50] as const;

/** The design's sort options, in its order and its words. */
const ORDER_CHOICES: readonly { readonly value: OfferOrder; readonly label: string }[] = [
  { value: "default", label: "Default order" },
  { value: "state", label: "State" },
  { value: "az", label: "Name A–Z" },
  { value: "za", label: "Name Z–A" },
];

/** The export formats the design's menu offers, in its order and its words. */
const EXPORTS = [
  { format: "csv", glyph: "description", label: "CSV" },
  { format: "xls", glyph: "table_view", label: "Excel (.xls)" },
  { format: "pdf", glyph: "picture_as_pdf", label: "PDF" },
] as const;

type ExportFormat = (typeof EXPORTS)[number]["format"];

export interface OfferMonitorProps {
  readonly transport: Transport;
  /** Innermost last: tenant, section, page. Rendered in the page body, never in the top bar. */
  readonly crumbs?: readonly string[];
  /** Injected so the lapse rule can be exercised without waiting for a countdown to run out. */
  readonly now?: () => number;
}

interface Loaded {
  readonly rows: readonly RideOffer[];
  /** DSP-02 answered a cursor, so this is the head of the list rather than all of it. */
  readonly truncated: boolean;
}

export function OfferMonitor({ transport, crumbs, now }: OfferMonitorProps): ReactNode {
  const [loaded, setLoaded] = useState<Loaded | undefined>(undefined);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [names, setNames] = useState<ReadonlyMap<string, string>>(new Map());
  const [search, setSearch] = useState("");
  const [selectedOfferId, setSelectedOfferId] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState<OfferStatus | undefined>(undefined);
  const [order, setOrder] = useState<OfferOrder>("default");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(10);
  const [exportOpen, setExportOpen] = useState(false);
  const exportRegion = useRef<HTMLSpanElement>(null);

  const load = useCallback(async () => {
    const client = offersApi.createRideOfferClient(transport);
    try {
      const answered = await client.getRideOffers(status, PER_FETCH, undefined);
      setLoaded({
        rows: answered.offers,
        truncated: typeof answered.nextAfter === "string" && answered.nextAfter.length > 0,
      });
      setFailure(undefined);
    } catch (error) {
      // One state rather than a banner over a half-rendered table. A board showing two of seven
      // offers with no explanation is worse than one showing none.
      setFailure(stateForError(error, ["ride-offer:read", "ride-offer:workload"]));
      setLoaded(undefined);
    }
  }, [transport, status]);

  /*
   * The names, separately and optionally.
   *
   * A second service under a second pair of scopes that `navigation.ts` does not grant this page,
   * so a refusal here must not take the board down with it. The design's cell is
   * `M. Achieng · drv_4410`; without the profiles it is `drv_4410`, which is the same cell with
   * the half that has no permission missing.
   */
  const loadNames = useCallback(async () => {
    try {
      const answered = await profilesApi
        .createDriverProfileClient(transport)
        .listDriverProfiles(undefined, PER_FETCH);
      setNames(
        new Map(
          (answered.driverProfiles as readonly DriverProfile[]).map((profile) => [
            profile.driverId,
            profile.displayName,
          ]),
        ),
      );
    } catch {
      setNames(new Map());
    }
  }, [transport]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void loadNames();
  }, [loadNames]);

  // The export menu closes on Escape and on a click outside, which the design file never needed to
  // handle because it is a reference rather than something an operator uses for a whole shift.
  useEffect(() => {
    if (!exportOpen) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setExportOpen(false);
    };
    const onPointer = (event: MouseEvent): void => {
      const region = exportRegion.current;
      if (region !== null && !region.contains(event.target as Node)) setExportOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return (): void => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [exportOpen]);

  const instant = now === undefined ? Date.now() : now();

  /*
   * The selected offer, across everything fetched rather than the page on screen.
   *
   * This board filters by effective status, which moves rows between filters as the clock passes
   * an expiry — so a selection resolved against the visible page would disappear on its own, with
   * the panel still open beside it.
   */
  const selectedOffer = loaded?.rows.find((offer) => offer.offerId === selectedOfferId);

  const view = useMemo(
    () => offerPage(loaded?.rows ?? [], { search, order, page, perPage, now: instant, names }),
    [loaded, search, order, page, perPage, instant, names],
  );

  /*
   * No `observedAt`, so no stale state.
   *
   * Every other board on this console can say how far behind it is: DSP-05 publishes a projection
   * watermark and the live map's layers publish per-row ages. DSP-02's listing publishes neither a
   * page-level instant nor a row-level one, so this screen cannot tell an operator that what they
   * are looking at is a minute old. Recorded in R-10 rather than faked from the moment the browser
   * asked, which would call the data fresh whenever the request succeeded.
   */
  const state = failure ?? stateForList(loaded?.rows);

  const clearFilters = (): void => {
    setSearch("");
    setStatus(undefined);
    setPage(1);
  };

  const bounded = boundedNote(
    loaded?.rows.length,
    loaded?.truncated ?? false,
    status,
    Number(PER_FETCH),
  );

  /*
   * The heading row, hoisted because the loading state draws it too.
   *
   * In `02 Console Shell v3` the headings sit outside the state switch: the design shimmers the
   * rows beneath a heading row that never moves, so the board does not jump when the data lands.
   * One element rather than two copies of this grid, which would be free to disagree. Q-287.
   */
  const head = (
    <div className="offers__head">
      <span>Offer</span>
      <span>Driver</span>
      <span>Result</span>
      <span className="offers__head--right">State</span>
    </div>
  );

  return (
    <section className="page offers">
      <PageHeading
        crumbs={crumbs ?? []}
        title="Offers"
        source="DSP-02"
        description="Offers sent to drivers and what happened to each."
        action={
          <button
            type="button"
            className="offers__primary"
            /*
             * Drawn, and refused — the same treatment R-08 gave the demand queue's `+ Manual
             * assign` and R-09 gave the live map's seven card buttons, for the same reason.
             *
             * The design's form is "Export offers" over a From/To range. DSP-02's listing takes
             * `status`, `limit` and `after` and nothing else: there is no date filter, no export
             * operation anywhere on the platform (R-01), and no total to bound a range against
             * (R-04). The toolbar's own Export below writes what this board holds, which is a
             * different and smaller promise than the form makes.
             */
            disabled
            title="DSP-02's listing takes no date range, and no operation exports offers. See R-10."
          >
            <span aria-hidden="true" className="offers__primary-glyph">
              add
            </span>
            Export offers
          </button>
        }
      />

      {/*
        No tiles, where the design has four.

        `Sent today`, `Accepted 71%`, `Timed out 18%` and `Declined 11%` are each a proportion of
        every offer made today, and not one of the three things that needs is published: DSP-02's
        listing carries no total (R-04), takes no date filter, and answers only its live three
        unless asked for one terminal status at a time. A count of the rows this page happens to
        hold under a label that means "today" would be a number an operator would work. R-10.
      */}

      {/*
        The grid and its panel in one positioned container, which is what docks the panel.

        `.detail` is docked two ways and both are written against `.board-split`: at 1240 and
        above `.board-split:has(> .detail)` makes it a 420px second column, and below 1240 the
        panel is `position: absolute; inset: 0 0 0 auto`, positioned against `.board-split`'s
        own `position: relative`. This board had no such wrapper, so an opened row drew a
        full-width block stacked under the table at 1440, and below 1240 an overlay anchored to
        the page rather than to the board. `DemandQueue.tsx` carries the measurements that found
        it: the first time any of these six panels was photographed, on 1 November.

        Which docking is right for these six at all is Q-297 — `19 WP18 Dispatch Operations` draws
        a centred modal where `04 Dispatch Exceptions Board` draws this column, and that is a
        decision rather than a defect. The wrapper is not that decision: it is what makes the
        build do what its own stylesheet already says.
      */}
      <div className="board-split">
        <div className="offers__panel">
          <div className="offers__toolbar">
            <span className="offers__search">
              <span aria-hidden="true" className="offers__search-glyph">
                search
              </span>
              <input
                id="offer-monitor-search"
                type="search"
                aria-label="Search"
                placeholder="Search"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
              />
            </span>
            <select
              id="offer-monitor-status"
              aria-label="Filter by state"
              value={status ?? ""}
              onChange={(event) => {
                setStatus(
                  event.target.value === "" ? undefined : (event.target.value as OfferStatus),
                );
                setPage(1);
              }}
            >
              {/* The design's word for the unfiltered option, over DSP-02's own seven rather than
                over the states present in the rows — see the module's FILTERABLE_STATUSES. */}
              <option value="">All states</option>
              {FILTERABLE_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {readableStatus(value)}
                </option>
              ))}
            </select>
            <select
              id="offer-monitor-order"
              aria-label="Sort"
              value={order}
              onChange={(event) => {
                setOrder(event.target.value as OfferOrder);
              }}
            >
              {ORDER_CHOICES.map((choice) => (
                <option key={choice.value} value={choice.value}>
                  {choice.label}
                </option>
              ))}
            </select>
            <span className="offers__export" ref={exportRegion}>
              <button
                type="button"
                className="offers__export-button"
                aria-expanded={exportOpen}
                onClick={() => {
                  setExportOpen((open) => !open);
                }}
              >
                <span aria-hidden="true" className="offers__export-glyph">
                  download
                </span>
                Export
              </button>
              {exportOpen ? (
                <div className="offers__export-menu" role="menu">
                  {EXPORTS.map((choice) => (
                    <button
                      key={choice.format}
                      type="button"
                      role="menuitem"
                      className="offers__export-item"
                      onClick={() => {
                        setExportOpen(false);
                        exportRows(view.all, choice.format, instant, names);
                      }}
                    >
                      <span aria-hidden="true" className="offers__export-item-glyph">
                        {choice.glyph}
                      </span>
                      {choice.label}
                    </button>
                  ))}
                </div>
              ) : null}
            </span>
          </div>

          <StateView
            state={state}
            purpose="Offers appear here as soon as the platform sends one to a driver."
            onRetry={() => {
              void load();
            }}
            skeleton={{
              head,
              columns: 4,
              rowClassName: "offers__row",
              // Both strings come from the trail the shell already draws, so the
              // sentence can never disagree with the breadcrumb above it.
              page: crumbs?.at(-1) ?? "this page",
              tenant: crumbs?.[0] ?? "this tenant",
            }}
          >
            {head}
            <div className="offers__rows">
              {view.rows.map((offer) => {
                const shown = effectiveStatus(offer, instant);
                const ahead = lapsed(offer, instant);
                return (
                  <div
                    key={offer.offerId}
                    className="offers__row"
                    data-selected={offer.offerId === selectedOfferId ? "true" : undefined}
                    role="button"
                    tabIndex={0}
                    aria-pressed={offer.offerId === selectedOfferId}
                    aria-label={`Open offer ${offer.offerId}`}
                    onClick={() => setSelectedOfferId(offer.offerId)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setSelectedOfferId(offer.offerId);
                      }
                    }}
                  >
                    <span className="offers__offer">{offer.offerId}</span>
                    <span className="offers__driver">{driverCell(offer, names)}</span>
                    <span className="offers__result">{resultText(offer, instant)}</span>
                    <span className="offers__state">
                      <StateBadge
                        code="DSP-02"
                        status={shown}
                        label={readableStatus(shown)}
                        /*
                         * The reason is on the badge rather than in a column of its own, because a
                         * column the design does not draw is design content invented here. An
                         * operator who wonders why a row they know was delivered says "timed out"
                         * gets the answer where they are already pointing.
                         */
                        {...(ahead
                          ? {
                              title: `Past its expiry at ${offer.expiresAt}. DSP-02 still records ${offer.status} until the expiry sweep runs; no transition is accepted after the expiry.`,
                            }
                          : {})}
                      />
                    </span>
                  </div>
                );
              })}
            </div>
            {view.total === 0 ? (
              <div className="offers__no-match">
                <span aria-hidden="true" className="offers__no-match-glyph">
                  search_off
                </span>
                Nothing matches this search or filter.
                <button type="button" className="offers__clear" onClick={clearFilters}>
                  Clear filters
                </button>
              </div>
            ) : null}
            <div className="offers__pager">
              <span>
                {view.total === 0
                  ? "No matching rows"
                  : `Showing ${(page - 1) * perPage + 1}–${Math.min(page * perPage, view.total)} of ${view.total}`}
              </span>
              <span className="offers__pager-controls">
                <select
                  id="offer-monitor-per-page"
                  aria-label="Rows per page"
                  value={perPage}
                  onChange={(event) => {
                    setPerPage(Number(event.target.value));
                    setPage(1);
                  }}
                >
                  {PER_PAGE_CHOICES.map((choice) => (
                    <option key={choice} value={choice}>
                      {choice} per page
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  aria-label="Previous page"
                  disabled={page <= 1}
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                >
                  <span aria-hidden="true">chevron_left</span>
                </button>
                <span className="offers__page">
                  Page {Math.min(page, view.pageCount)} of {view.pageCount}
                </span>
                <button
                  type="button"
                  aria-label="Next page"
                  disabled={page >= view.pageCount}
                  onClick={() => setPage((current) => Math.min(view.pageCount, current + 1))}
                >
                  <span aria-hidden="true">chevron_right</span>
                </button>
              </span>
            </div>
          </StateView>

          {/*
          What this board is actually showing, said rather than hidden.

          The same line the demand queue draws for the same reason, and here it carries the more
          important of the two facts. DSP-02's unfiltered listing is its live three, so "All states"
          is every offer still open and not every offer — and an operator who reads it as every
          offer concludes that nothing was accepted all morning. One line, at most: a chosen state
          that came back whole needs neither half of it.
        */}
          {bounded === undefined ? null : (
            <p className="offers__bounded" role="status">
              {bounded}
            </p>
          )}
        </div>

        {/*
        The selected offer. Found across everything fetched rather than the current page, so the
        panel does not describe a row the grid has just filtered away.
      */}
        {selectedOffer === undefined ? null : (
          <RecordDetail
            record={offerDetail(selectedOffer, names, instant)}
            open
            onClose={() => setSelectedOfferId(undefined)}
          />
        )}
      </div>

      <div className="offers__note">
        <span aria-hidden="true" className="offers__note-glyph">
          info
        </span>
        <span>
          Timed-out offers don't count against a driver's acceptance rate when their location was
          stale.
        </span>
      </div>
    </section>
  );
}

/**
 * Export what the operator is looking at, in the columns they are looking at.
 *
 * The filtered and ordered set rather than the page, and the words on screen rather than the raw
 * enum — including the lapse correction, so an exported row says the same thing the board did.
 * Excel takes an HTML table, which is what `application/vnd.ms-excel` has always meant in practice,
 * and PDF is the browser's own print dialogue.
 */
function exportRows(
  rows: readonly RideOffer[],
  format: ExportFormat,
  now: number,
  names: ReadonlyMap<string, string>,
): void {
  const header = ["Offer", "Driver", "Result", "State"];
  const body = rows.map((offer) => [
    offer.offerId,
    driverCell(offer, names),
    resultText(offer, now),
    readableStatus(effectiveStatus(offer, now)),
  ]);

  if (format === "csv") {
    const quote = (value: string): string => `"${value.replaceAll('"', '""')}"`;
    const csv = [header, ...body].map((line) => line.map(quote).join(",")).join("\n");
    download(new Blob([csv], { type: "text/csv" }), "offers.csv");
    return;
  }

  const table =
    '<table border="1" cellpadding="6" style="border-collapse:collapse;font-family:sans-serif;font-size:12px">' +
    `<tr>${header.map((cell) => `<th>${escapeHtml(cell)}</th>`).join("")}</tr>` +
    body
      .map((line) => `<tr>${line.map((cell) => `<td>${escapeHtml(cell)}</td>`).join("")}</tr>`)
      .join("") +
    "</table>";

  if (format === "xls") {
    download(
      new Blob([`<html><head><meta charset="utf-8"></head><body>${table}</body></html>`], {
        type: "application/vnd.ms-excel",
      }),
      "offers.xls",
    );
    return;
  }

  const printable = window.open("", "_blank");
  if (printable === null) return;
  printable.document.write(
    `<html><head><title>offers</title></head><body><h2 style="font-family:sans-serif">offers</h2>${table}</body></html>`,
  );
  printable.document.close();
  printable.focus();
  printable.print();
}

function escapeHtml(value: string): string {
  return value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;");
}

function download(blob: Blob, name: string): void {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = name;
  document.body.append(link);
  link.click();
  setTimeout(() => {
    URL.revokeObjectURL(link.href);
    link.remove();
  }, 500);
}
